import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { User, TrendingUp, TrendingDown } from 'lucide-react'

interface DriverScore {
  driver_id: string
  driver_name: string
  safety_score: number
  score_change: number
  total_miles: number
  events_count: number
  harsh_braking: number
  speeding: number
  rapid_acceleration: number
  clean_days: number
}

export function DriverScorecards() {
  const { data, isLoading } = useQuery({
    queryKey: ['driver-scores'],
    queryFn: async () => {
      const res = await fetch('/api/safety/driver-scores')
      if (!res.ok) throw new Error('Failed to fetch driver scores')
      return res.json() as Promise<{ drivers: DriverScore[] }>
    },
    refetchInterval: 60000,
  })

  const getScoreVariant = (score: number) => {
    if (score >= 90) return 'default'
    if (score >= 75) return 'secondary'
    return 'destructive'
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <User className="h-5 w-5" />
          Driver Scorecards
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">
            Loading driver scores...
          </div>
        ) : !data?.drivers || data.drivers.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No driver data available
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {data.drivers.map((driver) => {
              const isImproving = driver.score_change > 0

              return (
                <Card key={driver.driver_id} className="hover:shadow-md transition-shadow">
                  <CardContent className="pt-6 space-y-3">
                    {/* Driver Header */}
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold">{driver.driver_name}</h3>
                        <p className="text-xs text-muted-foreground mt-1">
                          {driver.total_miles.toLocaleString()} miles
                        </p>
                      </div>
                      <Badge variant={getScoreVariant(driver.safety_score)}>
                        {driver.safety_score}
                      </Badge>
                    </div>

                    {/* Score Progress */}
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-muted-foreground">Safety Score</span>
                        <span className={`flex items-center gap-1 ${isImproving ? 'text-green-600' : 'text-red-600'}`}>
                          {isImproving ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                          {isImproving ? '+' : ''}{driver.score_change}
                        </span>
                      </div>
                      <Progress value={driver.safety_score} className="h-2" />
                    </div>

                    {/* Events Summary */}
                    <div className="space-y-1 pt-2 border-t">
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Total Events</span>
                        <span className="font-medium">{driver.events_count}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Harsh Braking</span>
                        <span>{driver.harsh_braking}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Speeding</span>
                        <span>{driver.speeding}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Rapid Accel.</span>
                        <span>{driver.rapid_acceleration}</span>
                      </div>
                    </div>

                    {/* Clean Days Badge */}
                    {driver.clean_days > 0 && (
                      <div className="pt-2 border-t">
                        <Badge variant="outline" className="w-full justify-center bg-green-500/10 text-green-700 border-green-500/20">
                          🎯 {driver.clean_days} clean days
                        </Badge>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
